import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import CatForm from "../../components/ui/CatForm";
import RutinaForm from "../../components/ui/RutinaForm";
import CalibTemplateForm from "../../components/ui/CalibTemplateForm";
import { toast } from "react-toastify";
import categoriasIniciales from "../../utils/seed/categorias";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";
import { useBulkSelection } from "../../hooks/useBulkSelection";
import { useAiMasivoJob } from "../../hooks/useAiMasivoJob";

const CategoriasPage = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredCategorias, setFilteredCategorias] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [importando, setImportando] = useState(false);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/cat";
  const planActual = localStorage.getItem("planActual") || "FREE";
  const isFreePlan = planActual === "FREE";
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedCatCalib, setSelectedCatCalib] = useState(null);

  const {
    job: rutinasJob,
    enProceso: generandoMasivo,
    iniciar: iniciarRutinasMasivo,
  } = useAiMasivoJob({
    tipo: "rutinas-masivo",
    endpoint: "/rutinas/generar-masivo",
    onCompleted: (job) => {
      fetchCategorias();
      if (job.estado === "completado") {
        const p = job.progreso || {};
        toast.success(
          `Rutinas: ${p.generadosIA || 0} con IA, ${p.reusadosOtraEmpresa || 0} reusadas, ${p.fallidos || 0} fallidas. Revisa antes de usar.`,
        );
      } else {
        toast.error(job.error || "La generación masiva de rutinas falló");
      }
    },
  });

  const handleOpenRutinaModal = (cat) => setSelectedCat(cat);
  const handleCloseRutinaModal = () => setSelectedCat(null);

  const handleRutinaAdded = (updatedCat) => {
    fetchCategorias();
    handleCloseRutinaModal();
  };

  const handleOpenCalibModal = (cat) => setSelectedCatCalib(cat);
  const handleCloseCalibModal = () => setSelectedCatCalib(null);

  useEffect(() => {
    fetchCategorias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategorias = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategorias(res.data);
      setFilteredCategorias(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtro de búsqueda
  useEffect(() => {
    const filtered = categorias.filter((cat) => {
      const term = search.toLowerCase();
      return (
        cat.name.toLowerCase().includes(term) ||
        cat.grupo?.toLowerCase().includes(term)
      );
    });
    setFilteredCategorias(filtered);
    setCurrentPage(1);
  }, [search, categorias]);

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategorias.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredCategorias.length / itemsPerPage);

  const visibleIds = currentItems.map((c) => c._id);
  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    allSelected,
    clearSelection,
    hasSelection,
  } = useBulkSelection(visibleIds);

  const handleEdit = (categoria) => {
    setSelectedCategoria(categoria);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedCategoria(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedCategoria) {
        await axios.put(`${API_URL}/${selectedCategoria._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Categoría actualizada correctamente ✅");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Categoría creada correctamente ✅");
      }
      setShowModal(false);
      fetchCategorias();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al guardar la categoría";
      toast.error(backendMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Categoría eliminada correctamente ✅");
      fetchCategorias();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar categoría";
      toast.error(backendMessage);
    }
  };

  const handleBulkDelete = async () => {
    if (
      !window.confirm(`¿Eliminar ${selectedIds.size} categoría(s) seleccionada(s)?`)
    )
      return;
    try {
      const res = await axios.delete(`${API_URL}/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedIds) },
      });
      if (res.data.deleted === 0) {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      clearSelection();
      fetchCategorias();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Error al eliminar categorías seleccionadas";
      toast.error(backendMessage);
    }
  };

  // 🔹 Importar todas las categorías iniciales en un solo request
  const handleImportarBase = async () => {
    if (!window.confirm("¿Deseas importar todas las categorías iniciales?"))
      return;

    try {
      setImportando(true);
      await axios.post(
        `${API_URL}/importar`,
        { categorias: categoriasIniciales },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Categorías importadas correctamente ✅");
      fetchCategorias();
    } catch (err) {
      toast.error("Ocurrió un error al importar las categorías");
    } finally {
      setImportando(false);
    }
  };

  const handleGenerarRutinasMasivo = async () => {
    if (isFreePlan || generandoMasivo) return;
    if (
      !window.confirm(
        "¿Generar con IA las rutinas de todas las categorías sin rutina? Se te notificará al terminar.",
      )
    )
      return;

    const resultado = await iniciarRutinasMasivo();
    if (resultado.duplicate) toast.info(resultado.message);
    else if (resultado.ok) toast.success(resultado.message);
    else toast.error(resultado.message);
  };

  const progresoRutinasTexto = generandoMasivo
    ? `Generando... ${rutinasJob?.progreso?.procesados ?? 0}/${rutinasJob?.progreso?.total ?? 0}`
    : "Generar todas con IA";

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Categorías</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full lg:w-auto">
            <input
              type="text"
              placeholder="Buscar categoría..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-full sm:flex-1 lg:w-64 lg:flex-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {hasPermission("categorias.editar") && (
              <div className="relative w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={handleGenerarRutinasMasivo}
                  disabled={isFreePlan || generandoMasivo}
                  className={`w-full sm:w-auto whitespace-nowrap ${
                    isFreePlan ? "bg-gray-300 text-gray-600 cursor-not-allowed" : ""
                  }`}
                >
                  {generandoMasivo && (
                    <LoaderPinwheel className="h-4 w-4 mr-1 animate-spin inline" />
                  )}
                  {progresoRutinasTexto}
                </Button>
                {isFreePlan && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow">
                    Solo planes de pago
                  </span>
                )}
              </div>
            )}
            {hasPermission("categorias.crear") && (
              <Button
                onClick={handleCreate}
                className="w-full sm:w-auto whitespace-nowrap"
              >
                + Nueva
              </Button>
            )}
          </div>
        </div>

        {/* 🔹 Botón importar si no hay categorías */}
        {!loading && categorias.length === 0 && (
          <div className="my-4 text-center">
            <Button onClick={handleImportarBase} disabled={importando}>
              {importando ? "Importando..." : "Importar base de categorías"}
            </Button>
          </div>
        )}

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        {hasSelection && hasPermission("categorias.eliminar") && (
          <div className="flex items-center justify-between mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <span className="text-sm text-red-700">
              {selectedIds.size} categoría(s) seleccionada(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={handleBulkDelete}
              >
                Eliminar seleccionados
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                {hasPermission("categorias.eliminar") && (
                  <th className="py-2 px-4 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-left">Tipo</th>
                <th className="py-2 px-4 text-center">Rutina</th>
                <th className="py-2 px-4 text-center">Calibración</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((c) => (
                <tr
                  key={c._id}
                  className={
                    c.conEquipos
                      ? "border-b hover:bg-gray-50 text-green-600"
                      : "border-b hover:bg-gray-50"
                  }
                >
                  {hasPermission("categorias.eliminar") && (
                    <td className="py-2 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c._id)}
                        onChange={() => toggleSelect(c._id)}
                      />
                    </td>
                  )}
                  <td className="py-2 px-4">{c.name}</td>
                  <td className="py-2 px-4">{c.grupo}</td>
                  <td className="py-2 px-4 text-center">
                    {c.rutinas && c.rutinas.length > 0 ? (
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                        Rutina registrada
                      </span>
                    ) : (
                      <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full">
                        Sin rutina
                      </span>
                    )}
                    {c.rutinasRevisado === false && (
                      <div className="mt-1">
                        <span className="bg-yellow-100 text-yellow-800 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                          ⚠️ Generado por IA — revisar antes de usar
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {c.tieneCalibTemplate ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                          Plantilla registrada
                        </span>
                        <button
                          className="text-xs text-blue-600 hover:underline"
                          onClick={() => handleOpenCalibModal(c)}
                        >
                          Editar
                        </button>
                      </div>
                    ) : (
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => handleOpenCalibModal(c)}
                      >
                        Configurar
                      </button>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {hasPermission("categorias.editar") && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenRutinaModal(c)}
                        >
                          Rutina
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(c)}
                        >
                          Editar
                        </Button>
                      </>
                    )}
                    {hasPermission("categorias.eliminar") && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                        onClick={() => handleDelete(c._id)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}

              {currentItems.length === 0 && categorias.length > 0 && (
                <tr>
                  <td
                    colSpan={hasPermission("categorias.eliminar") ? "6" : "5"}
                    className="text-center py-4 text-gray-500 italic"
                  >
                    No se encontraron categorías con la búsqueda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentItems.length} de {filteredCategorias.length}{" "}
              resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {showModal && (
          <CatForm
            cat={selectedCategoria}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}
        {selectedCat && (
          <RutinaForm
            cat={selectedCat}
            onClose={handleCloseRutinaModal}
            onRutinaAdded={handleRutinaAdded}
          />
        )}
        {selectedCatCalib && (
          <CalibTemplateForm
            cat={selectedCatCalib}
            onClose={handleCloseCalibModal}
            categorias={categorias}
            onTemplateSaved={fetchCategorias}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CategoriasPage;
